import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';

import LeadForm from './LeadForm';
import LeadCaptureForm from './LeadCaptureForm';
import { MMLogo, MMLogoMark } from '@/components/brand/mm-logo';
import { Card, CardContent } from '@/components/ui/card';

import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ── Theming ────────────────────────────────────────────────────────────────
// The app uses shadcn HSL-channel tokens (--primary: "H S% L%"), so a saved
// form theme (hex colours) is converted to HSL and written onto :root. Only
// what the tenant chose is overridden; the rest keeps the shipped defaults.
function hexToHsl(hex) {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return { h: Math.round(hue * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}
const hslStr = (o) => (o ? `${o.h} ${o.s}% ${o.l}%` : null);
const clamp = (n) => Math.max(0, Math.min(100, Math.round(n)));

// Readable (#fff / near-black) text on top of a solid colour.
function contrastHex(hex) {
  const o = hexToHsl(hex);
  return o && o.l > 60 ? '#18181B' : '#FFFFFF';
}

function applyEmbedTheme(theme) {
  if (!theme || typeof theme !== 'object') return;
  const root = document.documentElement;
  const setHsl = (name, hex) => { const v = hslStr(hexToHsl(hex)); if (v) root.style.setProperty(name, v); };
  const setRaw = (name, h, s, l) => root.style.setProperty(name, `${h} ${s}% ${clamp(l)}%`);

  // Everything the form paints that the tenant does NOT pick - borders, input
  // outlines, placeholder text, the tinted question cards - used to keep its
  // light default. So choosing a dark background produced a form with pale grey
  // blocks and near-invisible labels, and the tenant reasonably concluded the
  // colour settings were broken. Derive them from the background instead.
  const bg = hexToHsl(theme.bg) || hexToHsl('#FAFAFB');
  const dark = bg.l < 50;
  const surfaceH = bg.h;
  const surfaceS = Math.min(bg.s, 20);

  if (theme.bg) {
    setHsl('--background', theme.bg);
    setRaw('--muted',      surfaceH, surfaceS, dark ? bg.l + 8  : bg.l - 4);
    setRaw('--secondary',  surfaceH, surfaceS, dark ? bg.l + 8  : bg.l - 4);
    setRaw('--accent',     surfaceH, surfaceS, dark ? bg.l + 12 : bg.l - 6);
    setRaw('--border',     surfaceH, surfaceS, dark ? bg.l + 16 : bg.l - 10);
    setRaw('--input',      surfaceH, surfaceS, dark ? bg.l + 16 : bg.l - 10);
    setRaw('--ring',       surfaceH, surfaceS, dark ? bg.l + 30 : bg.l - 30);
  }
  if (theme.card) { setHsl('--card', theme.card); setHsl('--popover', theme.card); }
  // The surface inside inputs. Left alone it follows the page background, which
  // is what the form has always done; setting it lets a dark form keep white
  // boxes to type in, the way most conversion-focused forms are built.
  const fieldHex = theme.input || theme.bg;
  if (fieldHex) {
    setHsl('--field', fieldHex);
    setHsl('--field-foreground', theme.inputText || (theme.input ? contrastHex(theme.input) : theme.text));
    // The input outline has to stand off the FIELD, not the page: a white box on
    // a dark form was getting the page's pale border and vanishing into itself.
    const f = hexToHsl(fieldHex);
    if (f && theme.input) setRaw('--input', f.h, Math.min(f.s, 20), f.l < 50 ? f.l + 16 : f.l - 12);
  }
  if (theme.text) {
    setHsl('--foreground', theme.text);
    setHsl('--card-foreground', theme.text);
    setHsl('--popover-foreground', theme.text);
    setHsl('--secondary-foreground', theme.text);
    setHsl('--accent-foreground', theme.text);
    const t = hexToHsl(theme.text);
    // Placeholder / helper text: a readable step off the body text, in whichever
    // direction the background allows.
    if (t) setRaw('--muted-foreground', t.h, Math.min(t.s, 15), dark ? t.l - 35 : t.l + 40);
  }
  if (theme.accent) {
    setHsl('--primary', theme.accent);
    const o = hexToHsl(theme.accent);
    // The tinted question card. A fixed 88% lightness is a pastel, which only
    // works on a light form. On a dark one, keeping the accent's saturation
    // turned an orange brand into chocolate-brown panels, so the tint keeps the
    // hue but drops nearly all of the saturation: a neutral panel with a hint of
    // warmth, rather than a colour nobody chose.
    if (o) setRaw('--primary-soft', o.h, dark ? Math.min(o.s, 12) : Math.max(20, o.s - 8), dark ? bg.l + 7 : 88);
    setHsl('--primary-foreground', theme.accentText || contrastHex(theme.accent));
  } else if (theme.accentText) {
    setHsl('--primary-foreground', theme.accentText);
  }
  // An explicit border always wins over the derived one.
  setHsl('--border', theme.border);
  if (theme.radius != null && theme.radius !== '') {
    const px = Number(theme.radius);
    if (Number.isFinite(px)) root.style.setProperty('--radius', `${px / 16}rem`);
  }
  root.dataset.questionStyle = theme.questionStyle === 'plain' ? 'plain' : 'card';
  if (theme.font) document.body.style.fontFamily = theme.font;
}

export default function App() {
  const [formConfig, setFormConfig] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  // PREVIEW MODE: the builder drives THIS form, so the preview cannot drift.
  //
  // The builder used to draw its own little picture of a form - a hand-made
  // card with invented questions ("Which country?") and a second, separate
  // implementation of the colour maths. It could only ever approximate, and it
  // did not show the real questions, the progress bar, the logo, the phone
  // field, the confirmation checkbox or the note under the button. A preview
  // that is not the thing is worse than no preview: people trust it.
  //
  // With ?preview=1 we render nothing from the server and wait for the parent
  // to post a draft config, then re-render on every keystroke it sends. The
  // preview is the form, because it IS the form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview') !== '1') return undefined;
    const onMessage = (e) => {
      const d = e.data;
      if (!d || d.type !== 'mm:preview:config' || !d.config) return;
      setFormConfig(d.config);
      applyEmbedTheme(d.config.theme);
      setUserId(d.config.assigned_user_id || d.config.created_by || 1);
      setLoading(false);
    };
    window.addEventListener('message', onMessage);
    // Tell the parent we are ready, so it does not post into a blank page.
    try { window.parent?.postMessage({ type: 'mm:preview:ready' }, '*'); } catch (_) { /* no parent */ }
    setLoading(false);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview') === '1') return;
    const formId = params.get('formId');
    const uid = params.get('userId');

    // URL-param userId still wins (kept for ad-hoc team links), but the
    // public embed URL no longer needs it - we fall back to the form's
    // assigned_user_id from the schema.
    const urlUid = uid ? parseInt(uid, 10) : null;

    if (formId) {
      axios
        .get(`${API_URL}/api/forms/${formId}`)
        .then((res) => {
          setFormConfig(res.data);
          applyEmbedTheme(res.data?.theme);
          if (urlUid) {
            setUserId(urlUid);
          } else if (res.data?.assigned_user_id) {
            setUserId(res.data.assigned_user_id);
          } else if (res.data?.created_by) {
            setUserId(res.data.created_by);
          }
        })
        .catch((err) => console.error('Form config fetch failed:', err))
        .finally(() => setLoading(false));
    } else {
      if (urlUid) setUserId(urlUid);
      setLoading(false);
    }
  }, []);

  // Pooled (round-robin) booking forms have no single assigned rep - the server
  // picks a free rep from the pool at booking time. So they can render without a
  // resolved userId, unlike a classic single-rep booking form.
  const isPool =
    formConfig?.assignment_mode === 'pool' &&
    Array.isArray(formConfig?.rep_pool) &&
    formConfig.rep_pool.length > 0;

  const theme = formConfig?.theme || {};
  const themed = !!theme.accent;
  // When a brand accent is set, tint the decorative halos to it so they don't
  // clash with the tenant's colours. Otherwise keep the default pink/green.
  const haloA = themed ? 'radial-gradient(circle at center, hsl(var(--primary-soft)) 0%, transparent 70%)'
    : 'radial-gradient(circle at center, #FED7E0 0%, transparent 70%)';
  const haloB = themed ? 'radial-gradient(circle at center, hsl(var(--primary-soft)) 0%, transparent 70%)'
    : 'radial-gradient(circle at center, #C7F0E0 0%, transparent 70%)';

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-3 text-muted-foreground">
          <MMLogoMark size={20} />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background">
      {/* Decorative pastel halos */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-32 size-[480px] rounded-full opacity-40 blur-3xl"
        style={{ background: haloA }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-32 size-[520px] rounded-full opacity-40 blur-3xl"
        style={{ background: haloB }}
      />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-2xl flex-col items-stretch px-4 py-6 sm:px-6 sm:py-8">
        {/* Tenant logo (only when they set one in the form's Design tab). */}
        {theme.logo ? (
          <div className="mb-4 flex justify-center">
            <img src={theme.logo} alt="" className="h-9 w-auto max-w-[200px] object-contain" />
          </div>
        ) : null}
        {/* Content - default logo removed so the calendar can use the full viewport.
            Branding moved into the footer as a single quiet line.

            A 'lead' form captures details only (no scheduling), so it renders
            without a userId. A 'booking' form needs a rep to book against, so
            it still requires a resolved userId. */}
        {formConfig?.form_type === 'lead' ? (
          <LeadCaptureForm userId={userId} formConfig={formConfig} />
        ) : (userId || isPool) ? (
          <LeadForm userId={userId} formConfig={formConfig} />
        ) : (
          <Card className="mx-auto w-full max-w-md shadow-elevated">
            <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-destructive-soft">
                <AlertCircle className="size-5 text-destructive" />
              </div>
              <h2 className="text-base font-semibold">Missing booking link</h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                This page expects a <code className="rounded bg-muted px-1 py-0.5 text-xs">userId</code> query parameter.
                Use the link your team shared, or contact us at{' '}
                <a href="mailto:hello@measuremarketer.com" className="text-primary hover:underline">
                  hello@measuremarketer.com
                </a>.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer - powered-by + copyright. Hidden when the tenant white-labels
            the form (theme.hideBranding). */}
        {theme.hideBranding ? null : (
          <footer className="mt-auto flex flex-col items-center gap-2 pt-6 text-[10px] text-muted-foreground">
            <MMLogo className="h-5 w-auto opacity-70" />
            <p>Powered by Measure Marketer · © {new Date().getFullYear()} Attribution-first CRM</p>
          </footer>
        )}
      </div>
    </div>
  );
}
